import React, { useState, useRef } from "react"
import { Link } from "react-router-dom"
import {
  Table,
  Col,
  Row,
  UncontrolledAlert,
  Spinner,
} from "reactstrap"
import api from "API/Api"
import {
  AvForm,
  AvField,
  AvRadioGroup,
  AvRadio,
} from "availity-reactstrap-validation"
import RatechartFile from "./../../../assets/Ratechart.csv"

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

const AddRatechart = () => {
  const formRef = useRef(null)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fatAndSnf, setFatAndSnf] = useState("")
  const [round, setRound] = useState(false)
  const [limit, setLimit] = useState(false)
  const [ts, setTs] = useState([{ tsUpTo: "", tsRate: "" }])
  const [minFat, setMinFat] = useState("")
  const [maxFat, setMaxFat] = useState("")
  const [minSnf, setMinSnf] = useState("")
  const [maxSnf, setMaxSnf] = useState("")
  const [fats, setFats] = useState([])
  const [snfs, setSnfs] = useState([])
  const [showTbl, setShowTbl] = useState(false)
  const [entry, setEntry] = useState("")
  const [csvData, setCsvData] = useState([])
  const [priceData, setPriceData] = useState([])
  const [importError, setImportError] = useState("")
  const [fatIncrement, setFatIncrement] = useState("0.1")
  const [snfIncrement, setSnfIncrement] = useState("0.1")

  const handleValidSubmit = async (event, values) => {
    setLoading(true)
    try {
      values.fat_snf = JSON.stringify(values.fat_snf)
      const resp = await api.post("create-rate-chart", values)
      if (resp.data.success === true) {
        formRef.current.reset()
        setSubmitted(true)
        setShowTbl(false)
        setFatAndSnf("")
        setEntry("")
      }
    } catch (error) {
      // Handled by global interceptor
    }
    setLoading(false)
  }

  const calcChangeHandler = e => {
    const v = e.target.value
    if (v == 1) setFatAndSnf(1)
    else if (v == 2 || v == 3) setFatAndSnf(2)
    else if (v == 4) setFatAndSnf(4)
    else if (v == 5) setFatAndSnf(5)
    else if (v == 6) setFatAndSnf(6)
    else setFatAndSnf(false)
  }

  const roundChangeHandler = e => {
    e.target.value == 1 ? setRound(true) : setRound(false)
  }
  const limitChangeHandler = e => {
    e.target.value == 1 ? setLimit(true) : setLimit(false)
  }
  const addRow = e => {
    e.preventDefault()
    setTs([...ts, { tsUpTo: "", tsRate: "" }])
  }
  const deleteRow = (e, i) => {
    const data = [...ts]
    data.splice(i, 1)
    setTs(data)
  }

  const calculatePrice = (minF, maxF, minS, maxS, fatInc, snfInc) => {
    const minFVal = parseFloat(minF)
    const maxFVal = parseFloat(maxF)
    const minSVal = parseFloat(minS)
    const maxSVal = parseFloat(maxS)
    const fatStep = parseFloat(fatInc || fatIncrement) || 0.1
    const snfStep = parseFloat(snfInc || snfIncrement) || 0.1

    if (isNaN(minFVal) || isNaN(maxFVal) || isNaN(minSVal) || isNaN(maxSVal)) {
      setImportError("Invalid FAT/SNF range values. Enter valid numbers.")
      return
    }
    if (minFVal >= maxFVal || minSVal >= maxSVal) {
      setImportError("Min values must be less than Max values.")
      return
    }
    if (fatStep <= 0 || snfStep <= 0) {
      setImportError("Increment must be greater than 0.")
      return
    }
    // Safety: prevent too many cells (max 200 rows × 200 cols)
    const fatCount = Math.floor((maxFVal - minFVal) / fatStep) + 1
    const snfCount = Math.floor((maxSVal - minSVal) / snfStep) + 1
    if (fatCount > 200 || snfCount > 200) {
      setImportError("Grid too large. Reduce the range or increase the increment.")
      return
    }

    setImportError("")
    setShowTbl(true)
    // Always use 2 decimal places — dairy standard for FAT/SNF
    let fat = []
    for (let i = minFVal; i <= maxFVal + 0.001; i += fatStep) {
      fat.push(i.toFixed(2))
    }
    let snf = []
    for (let i = minSVal; i <= maxSVal + 0.001; i += snfStep) {
      snf.push(i.toFixed(2))
    }
    setFats(fat)
    setSnfs(snf)
  }

  const entriesChangeHandler = e => {
    setEntry(parseInt(e.target.value))
    setImportError("")
  }

  const fileChangeHandler = e => {
    setImportError("")
    const file = e.target.files[0]
    if (!file) return

    // File size check
    if (file.size > MAX_FILE_SIZE) {
      setImportError("File too large. Maximum size is 5MB.")
      e.target.value = ""
      return
    }

    // File type check
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setImportError("Invalid file type. Only .csv files are supported.")
      e.target.value = ""
      return
    }

    const reader = new FileReader()
    reader.onerror = () => {
      setImportError("Failed to read file. Please try again.")
    }
    reader.onload = function (evt) {
      try {
        // Clean \r\n → \n, then split
        const rawText = evt.target.result.replace(/\r\n/g, "\n").replace(/\r/g, "\n")
        const lines = rawText.split("\n").filter(line => line.trim() !== "")

        if (lines.length < 2) {
          setImportError("CSV file must have at least a header row and one data row.")
          return
        }

        const headers = lines[0].split(",").map(h => h.trim())
        
        // Validate header format
        if (headers[0] !== "FAT/SNF") {
          setImportError('Invalid CSV format. First column header must be "FAT/SNF". Download the sample file for reference.')
          return
        }

        const result = []
        const rel_data = []
        let parseErrors = []

        for (let i = 1; i < lines.length; i++) {
          const currentline = lines[i].split(",").map(v => v.trim())
          if (currentline.length !== headers.length) {
            parseErrors.push(`Row ${i}: has ${currentline.length} columns, expected ${headers.length}`)
            continue
          }

          const obj = {}
          for (let j = 0; j < headers.length; j++) {
            obj[headers[j]] = currentline[j]
          }

          // Validate that FAT/SNF key exists and all values are numeric
          const fatVal = obj["FAT/SNF"]
          if (!fatVal || isNaN(parseFloat(fatVal))) {
            parseErrors.push(`Row ${i}: Invalid FAT value "${fatVal}"`)
            continue
          }

          // Validate rate values are positive numbers
          for (let j = 1; j < headers.length; j++) {
            const rate = parseFloat(obj[headers[j]])
            if (isNaN(rate) || rate < 0) {
              parseErrors.push(`Row ${i}, Col ${headers[j]}: Invalid rate "${obj[headers[j]]}"`)
            }
          }

          result.push(obj)
          if (fatVal) rel_data[fatVal] = obj
        }

        if (parseErrors.length > 0 && result.length === 0) {
          setImportError("CSV parsing failed:\n" + parseErrors.slice(0, 5).join("\n"))
          return
        }

        if (result.length === 0) {
          setImportError("No valid data rows found in CSV.")
          return
        }

        const fat_keys = Object.keys(rel_data)
        const snf_keys = Object.keys(rel_data[fat_keys[0]]).filter(k => k !== "FAT/SNF")

        setCsvData(rel_data)
        setPriceData(result)
        setMinFat(fat_keys[0])
        setMaxFat(fat_keys[fat_keys.length - 1])
        setMinSnf(snf_keys[0])
        setMaxSnf(snf_keys[snf_keys.length - 1])
        calculatePrice(fat_keys[0], fat_keys[fat_keys.length - 1], snf_keys[0], snf_keys[snf_keys.length - 1])
        setEntry(1)

        if (parseErrors.length > 0) {
          setImportError(`Imported with ${parseErrors.length} warnings. ${parseErrors.length} rows skipped.`)
        }
      } catch (err) {
        setImportError("Failed to parse CSV: " + err.message)
      }
    }
    reader.readAsText(file)
  }

  return (
    <>
      <div className="dashBody">
        <div className="font-size-18 mb-0 dashHeader">
          <Row>
            <Col md={6}>
              <h4>Add Rate Chart</h4>
            </Col>
            <Col md={6} style={{ textAlign: "right" }}>
              <Link to="/rate-charts" className="dashBtn">
                <i className=" fas fa-chevron-left "></i> <b> Back</b>
              </Link>
            </Col>
          </Row>
        </div>
        <div className="dashContent">
          <AvForm
            style={{ position: "relative" }}
            ref={formRef}
            className="form-horizontal"
            onValidSubmit={(e, v) => {
              handleValidSubmit(e, v)
            }}
          >
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    type="select"
                    name="milkType"
                    label="Milk Type"
                    required
                  >
                    <option value="">Select Milk Type</option>
                    <option value="1">Cow Milk</option>
                    <option value="2">Buffalo Milk</option>
                    <option value="3">Common Milk</option>
                  </AvField>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    name="ratechartName"
                    label="Rate Chart Name"
                    value=""
                    className="form-control"
                    type="text"
                    required
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    type="select"
                    name="calculationType"
                    label="Calculation Type"
                    required
                    onChange={e => calcChangeHandler(e)}
                  >
                    <option value="">Select Calculation Type</option>
                    <option value="1">FAT &amp; SNF</option>
                    <option value="2">TS Based (FAT &amp; SNF)</option>
                    <option value="3">TS Based (SNF Only)</option>
                    <option value="4">FAT Only</option>
                    <option value="5">KG FAT Rate</option>
                    <option value="6">Flat Rate</option>
                  </AvField>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <AvRadioGroup
                    inline
                    name="entries"
                    label="Entries"
                    required
                    errorMessage="Pick one!"
                    onChange={e => entriesChangeHandler(e)}
                  >
                    <AvRadio label="Manual" value="1" />
                    <AvRadio label="CSV Upload" value="2" />
                  </AvRadioGroup>
                </div>
              </Col>

              {/* CSV Upload */}
              {fatAndSnf == 1 && entry == 2 ? (
                <>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvField
                        name="file"
                        label="Upload (CSV Files Only)"
                        accept=".csv"
                        value=""
                        onChange={e => fileChangeHandler(e)}
                        className="form-control"
                        type="file"
                      />
                      <p style={{marginTop:'4px', fontSize:'13px'}}>
                        Sample CSV file{" "}
                        <Link to={RatechartFile} target="_blank" download style={{fontWeight:'600'}}>
                          ⬇ Download
                        </Link>
                      </p>
                    </div>
                    <AvField
                      name="fat_snf"
                      value={priceData}
                      className="form-control"
                      type="hidden"
                    />
                  </Col>
                </>
              ) : null}

              {/* Import Error Display */}
              {importError ? (
                <Col md={12}>
                  <UncontrolledAlert color="danger" style={{whiteSpace:'pre-line'}}>
                    <strong>⚠ Import Issue: </strong>{importError}
                  </UncontrolledAlert>
                </Col>
              ) : null}

              {/* Round Off Options (FAT & SNF type) */}
              {fatAndSnf == 1 ? (
                <>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup
                        inline
                        name="roundOff1"
                        label="Round off to 1 decimal if more than 1 decimal is available in FAT or SNF?"
                        required
                        errorMessage="Pick one!"
                        onChange={e => roundChangeHandler(e)}
                      >
                        <AvRadio label="Yes" value="1" />
                        <AvRadio label="No" value="0" />
                      </AvRadioGroup>
                    </div>
                  </Col>
                  {round ? (
                    <Col md={6}>
                      <div className="mb-3">
                        <AvRadioGroup
                          inline
                          name="upperRoundOff"
                          label="Upper round off if 2nd decimal is ≥"
                          required
                          errorMessage="Pick one!"
                        >
                          <AvRadio label="5" value="5" />
                          <AvRadio label="6" value="6" />
                          <AvRadio label="7" value="7" />
                          <AvRadio label="8" value="8" />
                          <AvRadio label="9" value="9" />
                        </AvRadioGroup>
                      </div>
                    </Col>
                  ) : null}
                </>
              ) : null}

              {/* Manual FAT×SNF Grid Entry */}
              {fatAndSnf == 1 && entry == 1 ? (
                <>
                  <Col md={12}>
                    <div className="mb-3">
                      <Row>
                        <Col md={1}><label>FAT Range</label></Col>
                        <Col md={3}>
                          <AvField
                            name="fatMin" value={minFat}
                            onChange={e => setMinFat(e.target.value)}
                            className="form-control" type="text" required
                            placeholder="Min e.g. 2.50"
                          />
                        </Col>
                        <Col md={3}>
                          <AvField
                            name="fatMax" value={maxFat}
                            onChange={e => setMaxFat(e.target.value)}
                            className="form-control" type="text" required
                            placeholder="Max e.g. 6.00"
                          />
                        </Col>
                        <Col md={1}><label>Increment</label></Col>
                        <Col md={4}>
                          <AvField
                            name="fatRange" className="form-control" type="text"
                            value={fatIncrement}
                            onChange={e => setFatIncrement(e.target.value)}
                            placeholder="e.g. 0.1, 0.2, 0.5"
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="mb-3">
                      <Row>
                        <Col md={1}><label>SNF Range</label></Col>
                        <Col md={3}>
                          <AvField
                            name="snfMin" value={minSnf}
                            className="form-control"
                            onChange={e => setMinSnf(e.target.value)}
                            type="text" required
                            placeholder="Min e.g. 6.50"
                          />
                        </Col>
                        <Col md={3}>
                          <AvField
                            name="snfMax" value={maxSnf}
                            onChange={e => setMaxSnf(e.target.value)}
                            className="form-control" type="text" required
                            placeholder="Max e.g. 8.00"
                          />
                        </Col>
                        <Col md={1}><label>Increment</label></Col>
                        <Col md={4}>
                          <AvField
                            name="snfRange" className="form-control" type="text"
                            value={snfIncrement}
                            onChange={e => setSnfIncrement(e.target.value)}
                            placeholder="e.g. 0.1, 0.2, 0.5"
                          />
                        </Col>
                      </Row>
                    </div>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={e => calculatePrice(minFat, maxFat, minSnf, maxSnf, fatIncrement, snfIncrement)}
                    >
                      Generate Grid
                    </button>
                  </Col>
                  {showTbl ? (
                    <div className="table-responsive mt-3">
                    <Table>
                      <thead>
                        <tr>
                          <th style={{position:'sticky', left:0, background:'#fff', zIndex:1}}>FAT/SNF</th>
                          {snfs.map(snf => (
                            <th key={snf}>{snf}</th>
                          ))}
                        </tr>
                        {fats.map(fat => (
                          <tr key={fat}>
                            <th style={{position:'sticky', left:0, background:'#f8f9fa', zIndex:1}}>{fat}</th>
                            {snfs.map((snf, i) => (
                              <th key={i}>
                                <AvField
                                  className="form-control"
                                  style={{padding:'2px 4px', minWidth:'60px', textAlign:'center'}}
                                  type="text"
                                  name={"fat_snf[" + fat + "][" + snf + "]"}
                                  value={csvData[fat] ? csvData[fat][snf] || "" : ""}
                                />
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                    </Table>
                    </div>
                  ) : null}
                </>
              ) : null}

              {/* TS Based Options */}
              {fatAndSnf == 2 || fatAndSnf == 5 ? (
                <>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup
                        inline name="fatSnfLimit"
                        label="Limit Minimum and Maximum FAT, SNF Values?"
                        required errorMessage="Pick one!"
                        onChange={e => limitChangeHandler(e)}
                      >
                        <AvRadio label="Yes" value="1" />
                        <AvRadio label="No" value="0" />
                      </AvRadioGroup>
                    </div>
                  </Col>
                  {limit ? (
                    <>
                      <Col md={6}>
                        <div className="mb-3">
                          <AvField name="minFat" label="Minimum FAT Eligible" value="" className="form-control" type="text" required />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <AvField name="maxFat" label="Maximum FAT Limit" value="" className="form-control" type="text" required />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <AvField name="minSnf" label="Minimum SNF Eligible" value="" className="form-control" type="text" required />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <AvField name="maxSnf" label="Maximum SNF Limit" value="" className="form-control" type="text" required />
                        </div>
                      </Col>
                    </>
                  ) : null}
                </>
              ) : null}

              {fatAndSnf == 2 ? (
                <>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup inline name="rateDecimalPrecision" label="Rate Decimal Precision" required errorMessage="Pick one!">
                        <AvRadio label="1" value="1" />
                        <AvRadio label="2" value="2" />
                      </AvRadioGroup>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup inline name="excessDecimal" label="Operation on Excess Decimal" required errorMessage="Pick one!">
                        <AvRadio label="Round Off" value="1" />
                        <AvRadio label="Truncate" value="2" />
                      </AvRadioGroup>
                    </div>
                  </Col>
                  <h5 className="mt-3 mb-3">Rate Chart TS Items Information</h5>
                  <Table className="table mb-0">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>TS Greater Than</th>
                        <th>TS Upto</th>
                        <th>TS Rate</th>
                        <th>
                          <button type="button" className="btn btn-primary btn-sm" onClick={e => addRow(e)}>
                            <i className="fa fa-plus"></i>
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ts.map((t, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td> </td>
                          <td>
                            <AvField name={`tsUpTo_${i}`} value="" className="form-control" type="text" required />
                          </td>
                          <td>
                            <AvField name={`tsRate_${i}`} value="" className="form-control" type="text" required />
                          </td>
                          <td>
                            {ts.length > 1 && (
                              <i className="fas fa-trash-alt icons icon-delete" onClick={e => deleteRow(e, i)} style={{cursor:'pointer'}}></i>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : null}

              {/* KG FAT Rate */}
              {fatAndSnf == 5 ? (
                <Col md={6}>
                  <div className="mb-3">
                    <AvField name="fatRate" label="KG FAT Rate (₹)" value="" className="form-control" type="text" required />
                  </div>
                </Col>
              ) : null}

              {/* Flat Rate */}
              {fatAndSnf == 6 ? (
                <Col md={6}>
                  <div className="mb-3">
                    <AvField name="flatRate" label="Flat Rate per Litre (₹)" value="" className="form-control" type="text" required />
                  </div>
                </Col>
              ) : null}
            </Row>

            <div className="mb-3 row mt-4">
              <div className="col-6">
                {submitted ? (
                  <UncontrolledAlert color="success">
                    <strong>Rate Chart Added Successfully!</strong>
                  </UncontrolledAlert>
                ) : null}
              </div>
              <div className="col-6 text-end">
                <button
                  className="btn btn-primary w-md waves-effect waves-light"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      <span style={{ paddingLeft: "10px" }}>Adding...</span>
                    </>
                  ) : (
                    <span>Add Rate Chart</span>
                  )}
                </button>
              </div>
            </div>
          </AvForm>
        </div>
      </div>
    </>
  )
}

export default AddRatechart
